import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  buildDiagnosticSummary,
  diagnoseSessionFetch,
  formatDiagnosticSummaryText,
  formatErrorCategory,
  getEnvironmentSnapshot,
} from "../../api/classicSiteEnvironmentDiagnostics";
import { CLASSIC_SITE_BASE } from "../../classicSite.constants";
import CsrfCookieDiagnosticDisplay from "../CsrfCookieDiagnosticDisplay";

function DiagnosticRow({ label, children }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

/**
 * Reusable environment / CORS / cookie diagnostic panel.
 * @param {{ diagnostic?: import('../../api/classicSiteEnvironmentDiagnostics').EnvironmentFetchDiagnostic|null, compact?: boolean, showTestButtons?: boolean }} props
 */
export default function SessionEnvironmentDiagnosticPanel({
  diagnostic = null,
  compact = false,
  showTestButtons = false,
}) {
  const snapshot = useMemo(() => getEnvironmentSnapshot(), []);
  const [includeResult, setIncludeResult] = useState(null);
  const [omitResult, setOmitResult] = useState(null);
  const [loading, setLoading] = useState(null);
  const [copied, setCopied] = useState(false);

  const activeDiagnostic = diagnostic || includeResult;

  const runInclude = useCallback(async () => {
    setLoading("include");
    const result = await diagnoseSessionFetch("include");
    setIncludeResult(result);
    setLoading(null);
    return result;
  }, []);

  const runOmit = useCallback(async () => {
    setLoading("omit");
    const result = await diagnoseSessionFetch("omit");
    setOmitResult(result);
    setLoading(null);
    return result;
  }, []);

  const openInTab = useCallback(() => {
    if (snapshot.sessionEndpointUrl) {
      window.open(snapshot.sessionEndpointUrl, "_blank", "noopener,noreferrer");
    }
  }, [snapshot.sessionEndpointUrl]);

  const copySummary = useCallback(async () => {
    const text = formatDiagnosticSummaryText(
      snapshot,
      includeResult || diagnostic,
      omitResult
    );
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [snapshot, includeResult, omitResult, diagnostic]);

  return (
    <div
      className={`cs-env-diag${compact ? " cs-env-diag--compact" : ""}`}
      role="region"
      aria-label="Environment and session diagnostics"
    >
      <h3 className="cs-env-diag__title">
        {compact ? "Environment diagnostics" : "Environment / CORS / Cookie Diagnostics"}
      </h3>

      <dl className="cs-detail__dl cs-env-diag__dl">
        <DiagnosticRow label="Frontend origin">
          <code>{snapshot.frontendOrigin || "—"}</code>
        </DiagnosticRow>
        <DiagnosticRow label="Backend origin">
          <code>{snapshot.backendOrigin || "—"}</code>
        </DiagnosticRow>
        <DiagnosticRow label="API base URL">
          <code>{snapshot.apiBaseUrl || "(not configured)"}</code>
        </DiagnosticRow>
        <DiagnosticRow label="Session endpoint">
          <code>{snapshot.sessionEndpointUrl || "—"}</code>
        </DiagnosticRow>
        <DiagnosticRow label="Same origin?">
          {snapshot.sameOrigin ? "Yes" : "No (cross-origin)"}
        </DiagnosticRow>
        <DiagnosticRow label="Expected credentials">
          <code>{snapshot.expectedCredentialsMode}</code>
        </DiagnosticRow>
        <CsrfCookieDiagnosticDisplay diagnostic={snapshot.csrfDiagnostic} compact />
        <DiagnosticRow label="Session cookie">
          <span className="cs-env-diag__note">{snapshot.sessionCookieNote}</span>
        </DiagnosticRow>
      </dl>

      {activeDiagnostic ? (
        <div
          className={`cs-env-diag__result${activeDiagnostic.ok ? " cs-env-diag__result--ok" : " cs-env-diag__result--fail"}`}
        >
          <h4>Last session fetch result</h4>
          <dl className="cs-detail__dl cs-env-diag__dl">
            <DiagnosticRow label="Error type">
              {activeDiagnostic.ok
                ? "Success"
                : formatErrorCategory(activeDiagnostic.errorCategory)}
            </DiagnosticRow>
            <DiagnosticRow label="HTTP status">
              {activeDiagnostic.status ?? "—"}
              {activeDiagnostic.statusText
                ? ` ${activeDiagnostic.statusText}`
                : ""}
            </DiagnosticRow>
            <DiagnosticRow label="Credentials mode">
              <code>{activeDiagnostic.credentials}</code>
            </DiagnosticRow>
            {activeDiagnostic.errorMessage ? (
              <DiagnosticRow label="Error message">
                <code>{activeDiagnostic.errorMessage}</code>
              </DiagnosticRow>
            ) : null}
            <DiagnosticRow label="Likely cause">
              {activeDiagnostic.likelyCause}
            </DiagnosticRow>
            <DiagnosticRow label="Recommended action">
              {activeDiagnostic.nextAction}
            </DiagnosticRow>
            {activeDiagnostic.responsePreview ? (
              <DiagnosticRow label="Response preview">
                <pre className="cs-env-diag__preview">
                  {activeDiagnostic.responsePreview}
                </pre>
              </DiagnosticRow>
            ) : null}
          </dl>
        </div>
      ) : null}

      {showTestButtons ? (
        <div className="cs-env-diag__actions">
          <button
            type="button"
            className="cs-btn cs-btn--ghost"
            disabled={loading !== null}
            onClick={runInclude}
          >
            {loading === "include"
              ? "Testing…"
              : "Test session fetch (credentials: include)"}
          </button>
          <button
            type="button"
            className="cs-btn cs-btn--ghost"
            disabled={loading !== null}
            onClick={runOmit}
          >
            {loading === "omit"
              ? "Testing…"
              : "Test session fetch (credentials: omit)"}
          </button>
          <button
            type="button"
            className="cs-btn cs-btn--ghost"
            disabled={!snapshot.sessionEndpointUrl}
            onClick={openInTab}
          >
            Open session endpoint in new tab
          </button>
          <button type="button" className="cs-link-btn" onClick={copySummary}>
            {copied ? "Copied" : "Copy diagnostic summary"}
          </button>
        </div>
      ) : null}

      {(includeResult || omitResult) && showTestButtons ? (
        <div className="cs-env-diag__compare">
          {includeResult ? (
            <p>
              <strong>include:</strong>{" "}
              {includeResult.ok
                ? `${includeResult.status} OK`
                : formatErrorCategory(includeResult.errorCategory)}
            </p>
          ) : null}
          {omitResult ? (
            <p>
              <strong>omit:</strong>{" "}
              {omitResult.ok
                ? `${omitResult.status} OK`
                : formatErrorCategory(omitResult.errorCategory)}
            </p>
          ) : null}
        </div>
      ) : null}

      {compact ? (
        <p className="cs-env-diag__footer">
          <Link to={`${CLASSIC_SITE_BASE}/diagnostics/staging-verification`}>
            Full environment diagnostics
          </Link>
        </p>
      ) : null}
    </div>
  );
}

export { buildDiagnosticSummary, getEnvironmentSnapshot };
